/**
 * Feb 2026 R293ze · FulfilmentBars — tiny per-line visual showing
 * ordered vs. remaining (for Quotation fulfilment) or invoiced vs.
 * remaining (for SO fulfilment). Pure presentation; takes the
 * normalised `lines` array shape both backend endpoints return.
 *
 * Props:
 *   lines     [{description, qty, qty_ordered|qty_invoiced, qty_remaining, fulfilled}]
 *   fulfilledKey  "qty_ordered" (quotation) | "qty_invoiced" (SO) — defaults by sniffing
 *   compact   boolean
 *   testid    string
 */

import { CheckCircle2 } from "lucide-react";

export default function FulfilmentBars({ lines = [], fulfilledKey, compact, testid }) {
  if (!lines.length) return null;
  const key = fulfilledKey || (lines[0]?.qty_invoiced !== undefined ? "qty_invoiced" : "qty_ordered");
  const verbLabel = key === "qty_invoiced" ? "Billed" : "Ordered";
  return (
    <div className={`space-y-${compact ? 1 : 2}`} data-testid={testid}>
      {lines.map((l) => {
        const total = Number(l.qty) || 0;
        const done = Number(l[key]) || 0;
        const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
        const full = total > 0 && done >= total - 1e-6;
        return (
          <div key={l.id} className="text-xs" data-testid={`fulfil-bar-${l.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="truncate flex-1 flex items-center gap-1">
                {full && <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />}
                <span className={full ? "text-muted-foreground line-through" : "text-foreground"}>
                  {l.description}
                </span>
              </div>
              <div className="font-mono text-[10px] tabular-nums text-muted-foreground shrink-0">
                {done} / {total} <span className="text-[9px]">{l.unit || ""}</span>
              </div>
            </div>
            <div className={`h-${compact ? 1 : 1.5} w-full bg-muted rounded-sm overflow-hidden mt-0.5`}
                 style={compact ? { height: 4 } : { height: 6 }}>
              <div
                className={`h-full transition-all ${full ? "bg-emerald-500" : "bg-sky-500"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {!compact && (
              <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-0.5">
                <span>{verbLabel} {done}</span>
                <span>Remaining <b className={l.qty_remaining > 0 ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}>{l.qty_remaining}</b></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
